import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Button,
  message,
  Card,
  Space,
  Result,
  Typography,
  Row,
  Col,
  Divider,
  Radio,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  AudioOutlined,
  StopOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import GeneralService from "./services/GeneralService";
import ReconnectingWebSocket from "reconnecting-websocket";
import RealtimeWebSocketService from "./services/RealtimeWebSocketService";
import lamejs from "lamejs";

const { Text, Paragraph } = Typography;

type DiffOpType = "equal" | "insert" | "delete";
interface DiffOp {
  type: DiffOpType;
  text: string;
}

function tokenizeWords(input: string): string[] {
  return input.trim().length === 0 ? [] : input.trim().split(/\s+/);
}

function computeWordDiff(aText: string, bText: string): DiffOp[] {
  const a = tokenizeWords(aText);
  const b = tokenizeWords(bText);
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0,
    j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "equal", text: a[i] });
      i++;
      j++;
    } else if (dp[i][j + 1] >= dp[i + 1][j]) {
      ops.push({ type: "insert", text: b[j] });
      j++;
    } else {
      ops.push({ type: "delete", text: a[i] });
      i++;
    }
  }
  while (i < n) {
    ops.push({ type: "delete", text: a[i++] });
  }
  while (j < m) {
    ops.push({ type: "insert", text: b[j++] });
  }
  // Merge consecutive ops of same type for readability
  const merged: DiffOp[] = [];
  for (const op of ops) {
    const last = merged[merged.length - 1];
    if (last && last.type === op.type) {
      last.text += " " + op.text;
    } else {
      merged.push({ ...op });
    }
  }
  return merged;
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  } catch {
    message.error("Failed to copy");
  }
}

interface TranscriptionResult {
  text: string;
  language: string;
  duration?: number;
}

interface UploadResponse {
  success: boolean;
  filename: string;
  transcription: TranscriptionResult;
  apiDuration?: number;
}

interface UploadComponentProps {
  prompt?: string;
  model?: string;
  panelLabel?: string;
  compareMode?: boolean;
  modelA?: string;
  modelB?: string;
}

const UploadComponent: React.FC<UploadComponentProps> = ({
  prompt,
  model,
  panelLabel,
  compareMode = false,
  modelA = "breeze",
  modelB = "breeze",
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [transcriptionResult, setTranscriptionResult] =
    useState<UploadResponse | null>(null);
  const [compareResultA, setCompareResultA] = useState<UploadResponse | null>(
    null
  );
  const [compareResultB, setCompareResultB] = useState<UploadResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Mode: file upload or live stream
  const [mode, setMode] = useState<"file" | "stream">("file");

  // Determine streaming model from props
  const getStreamingModel = (modelName: string): "breeze" | "nova-3" => {
    if (modelName === "deepgram:nova-3") return "nova-3";
    return "breeze"; // Default to breeze for other models
  };

  const streamingModel = getStreamingModel(model || "breeze");

  // Live streaming state
  const [isRecording, setIsRecording] = useState(false);
  const [cumulativeTranscript, setCumulativeTranscript] = useState<string>("");
  const [partialTranscript, setPartialTranscript] = useState<string>("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSpeakingRef = useRef(false);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const realtimeWsRef = useRef<RealtimeWebSocketService | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const pcmChunksRef = useRef<Int16Array[]>([]);

  // Compare-mode streaming transcripts and state
  const [streamCompareTextA, setStreamCompareTextA] = useState<string>("");
  const [streamCompareTextB, setStreamCompareTextB] = useState<string>("");
  const [compareUploading, setCompareUploading] = useState(false);

  // Timer effect
  useEffect(() => {
    if (uploading) {
      startTimeRef.current = Date.now();
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime((Date.now() - startTimeRef.current) / 1000);
        }
      }, 100); // Update every 100ms for smooth display
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [uploading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (realtimeWsRef.current) {
        realtimeWsRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    return seconds.toFixed(1) + "s";
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return seconds.toFixed(2) + "s";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);

    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    setError(null);
    setTranscriptionResult(null);
    setCompareResultA(null);
    setCompareResultB(null);

    try {
      if (!compareMode) {
        const formData = new FormData();
        formData.append("file", fileList[0] as any);

        // Add prompt and model if provided
        if (prompt) {
          formData.append("prompt", prompt);
        }
        if (model) {
          formData.append("model", model);
        }

        // Track API call duration
        const startTime = performance.now();
        const result = await GeneralService.postFormData(
          "/breeze/upload",
          formData,
          setUploading
        );
        const endTime = performance.now();
        const apiDuration = (endTime - startTime) / 1000; // Convert to seconds

        if (result.success) {
          // Add API duration to the result
          const resultWithDuration = {
            ...result,
            apiDuration: apiDuration,
          };
          setTranscriptionResult(resultWithDuration);
          message.success("File uploaded and transcribed successfully!");
          setFileList([]);
        } else {
          setError(result.error || "Upload failed");
          message.error(result.error || "Upload failed");
        }
      } else {
        // Compare mode: run two parallel requests against modelA and modelB with the same file
        const runOne = async (targetModel: string) => {
          const fd = new FormData();
          fd.append("file", fileList[0] as any);
          if (prompt) fd.append("prompt", prompt);
          if (targetModel) fd.append("model", targetModel);
          const start = performance.now();
          const res = await GeneralService.postFormData("/breeze/upload", fd);
          const end = performance.now();
          const apiDuration = (end - start) / 1000;
          if (res.success) {
            return { ...res, apiDuration } as UploadResponse;
          } else {
            throw new Error(res.error || "Upload failed");
          }
        };

        try {
          const [resA, resB] = await Promise.all([
            runOne(modelA),
            runOne(modelB),
          ]);
          setCompareResultA(resA);
          setCompareResultB(resB);
          message.success("Both transcriptions complete!");
          setFileList([]);
        } catch (e: any) {
          const msg = e?.message || "Upload failed";
          setError(msg);
          message.error(msg);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const initializeBreezeWebSocket = () => {
    if (wsRef.current) return;

    wsRef.current = new ReconnectingWebSocket(
      `${GeneralService.BREEZE_ENDPOINT}/breeze/ws`,
      [],
      {
        maxRetries: 10,
        reconnectionDelayGrowFactor: 1.3,
        maxReconnectionDelay: 10000,
        minReconnectionDelay: 1000,
      }
    );

    wsRef.current.onopen = () => {
      setIsConnected(true);
      message.success("Connected to Breeze server");
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
      message.error("Disconnected from Breeze server");
    };

    wsRef.current.onerror = () => {
      message.error("WebSocket error occurred");
    };

    wsRef.current.onmessage = (event: MessageEvent) => {
      try {
        const response = JSON.parse(event.data);
        // Handle VAD messages
        if (response.type === "vad") {
          console.log(JSON.stringify(response, null, 2));
          const vadEvent: string | undefined = response.event;
          if (vadEvent === "speech_start") {
            setIsSpeaking(true);
            pcmChunksRef.current = [];
            setCumulativeTranscript("");
            setPartialTranscript("");
            setStreamCompareTextA("");
            setStreamCompareTextB("");
          } else if (vadEvent === "pause") {
            setIsSpeaking(false);
            void handlePauseUpload();
            if (compareMode && isRecording) {
              stopStreaming();
            }
          }
          return;
        }
        if (response.status === "success") {
          const transcript: string | undefined = response.transcript;
          const isFinal: boolean = !!response.is_final;
          if (transcript) {
            if (isFinal) {
              setCumulativeTranscript(
                (prev) => (prev ? prev + " " : "") + transcript
              );
              setPartialTranscript("");
            } else {
              setPartialTranscript(transcript);
            }
          }
        } else if (response.status === "error") {
          message.error(response.error || "Transcription error occurred");
        }
      } catch {
        message.error("Error processing server response");
      }
    };
  };

  const initializeRealtimeWebSocket = async () => {
    if (realtimeWsRef.current) return;

    realtimeWsRef.current = new RealtimeWebSocketService({
      onConnectionEstablished: () => {
        setIsConnected(true);
        message.success("Connected to Deepgram transcription service");
      },
      onConnectionClosed: () => {
        setIsConnected(false);
        message.error("Disconnected from transcription service");
      },
      onTranscriptionDelta: (text) => {
        setPartialTranscript((prev) => prev + text);
      },
      onTranscriptionComplete: (text) => {
        console.log("Completed:", text);
        setCumulativeTranscript((prev) => (prev ? prev + " " : "") + text);
        setPartialTranscript("");
      },
      onVADEvent: (event, itemId) => {
        console.log(`VAD event: ${event}`, itemId);
        if (event === "speech_started") {
          setIsSpeaking(true);
          pcmChunksRef.current = [];
          setCumulativeTranscript("");
          setPartialTranscript("");
          setStreamCompareTextA("");
          setStreamCompareTextB("");
        } else if (event === "speech_stopped") {
          setIsSpeaking(false);
        }
      },
      onError: (error) => {
        console.error("Transcription error:", error);
        message.error(`Transcription error: ${error}`);
      },
    });

    try {
      console.log("🔌 Connecting to realtime WebSocket...");
      await realtimeWsRef.current.connect();
      console.log("✅ WebSocket connected, configuring session...");

      await realtimeWsRef.current.configureSession({
        model: model || "nova-3", // Use the model from props (cleaned up)
        prompt: prompt || "",
        vadThreshold: 0.5,
        prefixPaddingMs: 300,
        silenceDurationMs: 500,
      });
      console.log("✅ Session configured successfully");
    } catch (error) {
      console.error("❌ Failed to initialize realtime WebSocket:", error);
      message.error("Failed to connect to transcription service");
      // Don't proceed with audio streaming if session setup failed
      return;
    }
  };

  const encodePcmChunksToMp3 = (chunks: Int16Array[], sampleRate: number) => {
    const encoder = new lamejs.Mp3Encoder(1, sampleRate, 64);
    const mp3Data: Uint8Array[] = [];
    for (const chunk of chunks) {
      const buf = encoder.encodeBuffer(chunk);
      if (buf && buf.length > 0) mp3Data.push(buf);
    }
    const end = encoder.flush();
    if (end && end.length > 0) mp3Data.push(end);
    return new Blob(mp3Data, { type: "audio/mpeg" });
  };

  const encodePcmChunksToWav = (chunks: Int16Array[], sampleRate: number) => {
    // Concatenate PCM chunks
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    const pcm = new Int16Array(totalLength);
    let offset = 0;
    for (const c of chunks) {
      pcm.set(c, offset);
      offset += c.length;
    }

    const bytesPerSample = 2; // 16-bit
    const blockAlign = 1 * bytesPerSample; // mono
    const byteRate = sampleRate * blockAlign;
    const dataSize = pcm.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // RIFF header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true); // PCM chunk size
    view.setUint16(20, 1, true); // Audio format = PCM
    view.setUint16(22, 1, true); // Channels = 1
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // Bits per sample
    writeString(36, "data");
    view.setUint32(40, dataSize, true);

    // PCM samples
    let idx = 44;
    for (let i = 0; i < pcm.length; i++, idx += 2) {
      view.setInt16(idx, pcm[i], true);
    }

    return new Blob([view], { type: "audio/wav" });
  };

  const handlePauseUpload = async () => {
    if (!compareMode) return;
    if (compareUploading) return;
    const targets: Array<{ label: "A" | "B"; model: string }> = [];
    if (modelA && modelA !== "breeze")
      targets.push({ label: "A", model: modelA });
    if (modelB && modelB !== "breeze")
      targets.push({ label: "B", model: modelB });
    if (targets.length === 0) return;

    const chunks = pcmChunksRef.current;
    if (!chunks || chunks.length === 0) return;

    try {
      setCompareUploading(true);
      let uploadBlob: Blob;
      let contentType: string;
      let filename: string;
      try {
        uploadBlob = encodePcmChunksToMp3(chunks, 16000);
        contentType = "audio/mpeg";
        filename = "segment.mp3";
      } catch {
        // Fallback to WAV if MP3 encoding fails (e.g., MPEGMode not defined)
        uploadBlob = encodePcmChunksToWav(chunks, 16000);
        contentType = "audio/wav";
        filename = "segment.wav";
      }
      const uploadOne = async (targetModel: string) => {
        const form = new FormData();
        form.append(
          "file",
          new File([uploadBlob], filename, { type: contentType })
        );
        if (prompt) form.append("prompt", prompt);
        if (targetModel) form.append("model", targetModel);
        const res = await GeneralService.postFormData("/breeze/upload", form);
        if (
          res &&
          res.success &&
          res.transcription &&
          res.transcription.text != null
        ) {
          return String(res.transcription.text);
        }
        throw new Error(res?.error || "Upload failed");
      };

      const results = await Promise.all(
        targets.map(async (t) => ({
          label: t.label,
          text: await uploadOne(t.model),
        }))
      );
      for (const r of results) {
        if (r.label === "A") {
          setStreamCompareTextA((prev) => (prev ? prev + " " : "") + r.text);
        } else {
          setStreamCompareTextB((prev) => (prev ? prev + " " : "") + r.text);
        }
      }
    } catch (e: any) {
      message.error(e?.message || "Compare upload failed");
    } finally {
      setCompareUploading(false);
    }
  };

  const startStreaming = async () => {
    if (isRecording) {
      console.log("🚫 Already recording, ignoring start request");
      return;
    }

    try {
      // Initialize the appropriate WebSocket based on streaming model
      if (streamingModel === "breeze") {
        initializeBreezeWebSocket();

        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          message.info("Connecting to Breeze server...");
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(
              () => reject(new Error("Connection timeout")),
              10000
            );
            const waitOpen = () => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(waitOpen, 100);
              }
            };
            waitOpen();
          });
        }
      } else {
        await initializeRealtimeWebSocket();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: streamingModel === "nova-3" ? 24000 : 16000,
        },
      });
      mediaStreamRef.current = stream;

      const sampleRate = streamingModel === "nova-3" ? 24000 : 16000;
      const audioContext = new AudioContext({
        sampleRate,
        latencyHint: "interactive",
      });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(
            -32768,
            Math.min(32767, Math.round(inputData[i] * 32767))
          );
        }

        if (streamingModel === "breeze") {
          // Breeze WebSocket protocol
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            if (isSpeakingRef.current) {
              pcmChunksRef.current.push(new Int16Array(pcmData));
            }
            const base64Audio = btoa(
              String.fromCharCode.apply(
                null,
                Array.from(new Uint8Array(pcmData.buffer)) as any
              )
            );
            const msg = {
              type: "audio",
              audio: base64Audio,
              format: "raw",
              sampleRate: 16000,
              channels: 1,
            };
            wsRef.current.send(JSON.stringify(msg));
          }
        } else {
          // Deepgram WebSocket protocol (nova-3)
          if (realtimeWsRef.current?.isWebSocketConnected()) {
            realtimeWsRef.current.sendAudio(pcmData.buffer);
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      if (streamingModel === "breeze") {
        wsRef.current?.send(
          JSON.stringify({
            type: "start",
            // Align with server/API usage: use `context` for the prompt
            context: prompt || undefined,
            full_conversation: undefined,
          })
        );
      }

      setIsRecording(true);
      // Clear transcripts only when starting a new recording session
      setCumulativeTranscript("");
      setPartialTranscript("");
      setStreamCompareTextA("");
      setStreamCompareTextB("");
      pcmChunksRef.current = [];
      setIsSpeaking(false);
      message.success(
        `Started recording with ${
          streamingModel === "nova-3" ? "Deepgram Nova-3" : "Breeze"
        }`
      );
    } catch (err) {
      if (err instanceof Error && err.message === "Connection timeout") {
        message.error(
          "Failed to connect to server. Please check if the server is running."
        );
      } else {
        message.error("Failed to access microphone. Please check permissions.");
      }
    }
  };

  const stopStreaming = () => {
    console.log("🛑 Stopping streaming...");

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsRecording(false);

    // Clean up the appropriate WebSocket service
    if (streamingModel === "breeze") {
      wsRef.current?.send(JSON.stringify({ type: "stop" }));
    } else {
      // For Deepgram, we just disconnect the service
      if (realtimeWsRef.current) {
        realtimeWsRef.current.disconnect();
        realtimeWsRef.current = null;
      }
    }

    message.success("Stopped recording");
  };

  const uploadProps: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      setTranscriptionResult(null);
      setError(null);
    },
    beforeUpload: (file) => {
      const lower = file.name.toLowerCase();
      const extOk =
        lower.endsWith(".mp3") ||
        lower.endsWith(".mp4") ||
        lower.endsWith(".mpeg") ||
        lower.endsWith(".mpga") ||
        lower.endsWith(".m4a") ||
        lower.endsWith(".wav") ||
        lower.endsWith(".webm") ||
        lower.endsWith(".ogg") ||
        lower.endsWith(".flac");
      const typeOk =
        file.type === "audio/mpeg" ||
        file.type === "audio/mp4" ||
        file.type === "audio/wav" ||
        file.type === "audio/webm" ||
        file.type === "audio/ogg" ||
        file.type === "audio/flac";
      if (!extOk && !typeOk) {
        message.error(
          "Unsupported audio format. Use mp3, wav, m4a, webm, ogg, or flac."
        );
        return false;
      }

      // Check file size (25MB limit)
      const isLt25M = file.size / 1024 / 1024 < 25;
      if (!isLt25M) {
        message.error("File size must be smaller than 25MB!");
        return false;
      }

      setFileList([file]);
      setTranscriptionResult(null);
      setError(null);
      return false;
    },
    fileList,
    multiple: false,
    accept:
      ".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm,.ogg,.flac,audio/mpeg,audio/mp4,audio/wav,audio/webm,audio/ogg,audio/flac",
  };

  const resetDemo = () => {
    setFileList([]);
    setTranscriptionResult(null);
    setCompareResultA(null);
    setCompareResultB(null);
    setError(null);
    setElapsedTime(0);
  };

  const hasSingleResult = !compareMode && !!transcriptionResult;
  const hasCompareResults = compareMode && !!compareResultA && !!compareResultB;
  const showUploadUI = !hasSingleResult && !hasCompareResults;
  const singleResult = transcriptionResult as UploadResponse | null;
  const resultA = compareResultA as UploadResponse | null;
  const resultB = compareResultB as UploadResponse | null;
  const displayTranscript = (
    cumulativeTranscript +
    (partialTranscript
      ? (cumulativeTranscript ? " " : "") + partialTranscript
      : "")
  ).trim();
  const liveCompareTextA =
    modelA === "breeze" ? displayTranscript : streamCompareTextA;
  const liveCompareTextB =
    modelB === "breeze" ? displayTranscript : streamCompareTextB;

  return (
    <div className="upload-center-container">
      {showUploadUI ? (
        <Card
          title={
            panelLabel ||
            (compareMode ? "Upload MP3 File (Compare)" : "Transcription Demo")
          }
          className="upload-center-card">
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              optionType="button"
              buttonStyle="solid">
              <Radio.Button value="file">MP3 File</Radio.Button>
              <Radio.Button value="stream">Live Stream</Radio.Button>
            </Radio.Group>

            {mode === "file" ? (
              <>
                <div>
                  <Text>
                    Select an MP3 file to upload and transcribe. Maximum file
                    size: 25MB.
                  </Text>
                </div>
                <Upload {...uploadProps} listType="text">
                  <Button icon={<UploadOutlined />}>Select MP3 File</Button>
                </Upload>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}>
                  <Button
                    type="primary"
                    onClick={handleUpload}
                    disabled={fileList.length === 0}
                    loading={uploading}
                    icon={<FileTextOutlined />}
                    size="large"
                    style={
                      fileList.length === 0
                        ? {}
                        : { backgroundColor: "#21b892", borderColor: "#21b892" }
                    }>
                    {uploading ? "Transcribing..." : "Upload & Transcribe"}
                  </Button>
                  {uploading && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                      <ClockCircleOutlined style={{ color: "#21b892" }} />
                      <Text style={{ color: "#21b892", fontWeight: "500" }}>
                        {formatTime(elapsedTime)}
                      </Text>
                    </div>
                  )}
                </div>
                {error && (
                  <div className="error-message">
                    <Text type="danger">{error}</Text>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <Text>
                    Stream live audio from your microphone and view the
                    transcription in real-time.
                  </Text>
                </div>
                <Button
                  type={isRecording ? "primary" : "default"}
                  danger={isRecording}
                  icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                  onClick={isRecording ? stopStreaming : startStreaming}
                  size="large"
                  block>
                  {isRecording ? "Stop Recording" : "Start Recording"}
                </Button>
                {wsRef.current && !isConnected && (
                  <Text type="warning">Connecting to server...</Text>
                )}
                {!compareMode && displayTranscript && (
                  <Card size="small" title="Transcription">
                    <Text>{displayTranscript}</Text>
                  </Card>
                )}
                {compareMode && (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Card
                          title={`Model A: ${modelA}`}
                          size="small"
                          extra={
                            compareUploading && (
                              <Text type="secondary">Transcribing…</Text>
                            )
                          }>
                          <Paragraph
                            className="transcription-text-center"
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              padding: "12px",
                              border: "1px solid #d9d9d9",
                              borderRadius: "6px",
                              backgroundColor: "#fafafa",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              margin: 0,
                            }}>
                            {modelA === "breeze"
                              ? displayTranscript
                              : streamCompareTextA}
                          </Paragraph>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <Card
                          title={`Model B: ${modelB}`}
                          size="small"
                          extra={
                            compareUploading && (
                              <Text type="secondary">Transcribing…</Text>
                            )
                          }>
                          <Paragraph
                            className="transcription-text-center"
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              padding: "12px",
                              border: "1px solid #d9d9d9",
                              borderRadius: "6px",
                              backgroundColor: "#fafafa",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              margin: 0,
                            }}>
                            {modelB === "breeze"
                              ? displayTranscript
                              : streamCompareTextB}
                          </Paragraph>
                        </Card>
                      </Col>
                    </Row>
                    {(liveCompareTextA || liveCompareTextB) && (
                      <>
                        <Divider style={{ margin: "16px 0" }} />
                        <Card
                          title={
                            <span>
                              Diff (<span style={{ color: "#cf1322" }}>A</span>→
                              <span style={{ color: "#08979c" }}>B</span>)
                            </span>
                          }
                          size="small"
                          bordered>
                          <Paragraph
                            style={{
                              maxHeight: "260px",
                              overflowY: "auto",
                              padding: "12px",
                              border: "1px solid #d9d9d9",
                              borderRadius: "6px",
                              backgroundColor: "#fafafa",
                              marginTop: "8px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}>
                            {computeWordDiff(
                              liveCompareTextA || "",
                              liveCompareTextB || ""
                            ).map((op, idx) => {
                              if (op.type === "equal") {
                                return <span key={idx}>{op.text} </span>;
                              }
                              if (op.type === "insert") {
                                return (
                                  <span
                                    key={idx}
                                    style={{
                                      backgroundColor: "#e6fffb",
                                      color: "#08979c",
                                    }}>
                                    {op.text}
                                  </span>
                                );
                              }
                              return (
                                <span
                                  key={idx}
                                  style={{
                                    backgroundColor: "#fff1f0",
                                    color: "#cf1322",
                                    textDecoration: "line-through",
                                  }}>
                                  {op.text}
                                </span>
                              );
                            })}
                          </Paragraph>
                        </Card>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Space>
        </Card>
      ) : hasSingleResult ? (
        <Result
          icon={<CheckCircleOutlined style={{ color: "#21b892" }} />}
          title="Transcription Complete!"
          subTitle={`File: ${singleResult!.filename}`}
          extra={[
            <Button
              type="primary"
              key="reset"
              onClick={resetDemo}
              style={{ backgroundColor: "#21b892", borderColor: "#21b892" }}>
              Upload Another File
            </Button>,
          ]}>
          <div className="transcription-result-center">
            <Card
              title={`${
                panelLabel ? panelLabel + " - " : ""
              }Transcription Details`}
              size="small">
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  <Text strong>Audio Duration: </Text>
                  <Text>
                    {singleResult!.transcription.duration !== undefined &&
                    singleResult!.transcription.duration !== null
                      ? formatDuration(singleResult!.transcription.duration)
                      : "-"}
                  </Text>
                </div>
                {singleResult!.apiDuration && (
                  <div>
                    <Text strong>Transcription Duration: </Text>
                    <Text>{formatDuration(singleResult!.apiDuration)}</Text>
                  </div>
                )}
                <div>
                  <Paragraph
                    className="transcription-text-center"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      padding: "12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      backgroundColor: "#fafafa",
                      marginTop: "8px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}>
                    {singleResult!.transcription.text}
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </div>
        </Result>
      ) : hasCompareResults ? (
        <Card
          title="Transcription Details (Compare)"
          headStyle={{ paddingTop: 12, paddingBottom: 12 }}
          extra={
            <Button
              type="primary"
              onClick={resetDemo}
              style={{ backgroundColor: "#21b892", borderColor: "#21b892" }}>
              Upload Another File
            </Button>
          }
          className="upload-center-card">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title={`Model A: ${modelA}`} size="small">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Audio Duration: </Text>
                    <Text>
                      {resultA!.transcription.duration !== undefined &&
                      resultA!.transcription.duration !== null
                        ? formatDuration(resultA!.transcription.duration)
                        : "-"}
                    </Text>
                  </div>
                  {resultA!.apiDuration && (
                    <div>
                      <Text strong>Transcription Duration: </Text>
                      <Text>{formatDuration(resultA!.apiDuration)}</Text>
                    </div>
                  )}
                  <div>
                    <div style={{ position: "relative", marginTop: "8px" }}>
                      <Button
                        type="text"
                        aria-label="Copy Model A transcription"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(resultA!.transcription.text)
                        }
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 1,
                        }}
                      />
                      <Paragraph
                        className="transcription-text-center"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "12px",
                          paddingTop: 36,
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                          backgroundColor: "#fafafa",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          margin: 0,
                        }}>
                        {resultA!.transcription.text}
                      </Paragraph>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={`Model B: ${modelB}`} size="small">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Audio Duration: </Text>
                    <Text>
                      {resultB!.transcription.duration !== undefined &&
                      resultB!.transcription.duration !== null
                        ? formatDuration(resultB!.transcription.duration)
                        : "-"}
                    </Text>
                  </div>
                  {resultB!.apiDuration && (
                    <div>
                      <Text strong>Transcription Duration: </Text>
                      <Text>{formatDuration(resultB!.apiDuration)}</Text>
                    </div>
                  )}
                  <div>
                    <div style={{ position: "relative", marginTop: "8px" }}>
                      <Button
                        type="text"
                        aria-label="Copy Model B transcription"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(resultB!.transcription.text)
                        }
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 1,
                        }}
                      />
                      <Paragraph
                        className="transcription-text-center"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "12px",
                          paddingTop: 36,
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                          backgroundColor: "#fafafa",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          margin: 0,
                        }}>
                        {resultB!.transcription.text}
                      </Paragraph>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
          <Divider style={{ margin: "16px 0" }} />
          <Card
            title={
              <span>
                Diff (<span style={{ color: "#cf1322" }}>{modelA}</span>→
                <span style={{ color: "#08979c" }}>{modelB}</span>)
              </span>
            }
            size="small"
            bordered>
            <Paragraph
              style={{
                maxHeight: "260px",
                overflowY: "auto",
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                marginTop: "8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}>
              {computeWordDiff(
                resultA!.transcription.text,
                resultB!.transcription.text
              ).map((op, idx) => {
                if (op.type === "equal") {
                  return <span key={idx}>{op.text} </span>;
                }
                if (op.type === "insert") {
                  return (
                    <span
                      key={idx}
                      style={{ backgroundColor: "#e6fffb", color: "#08979c" }}>
                      {op.text}
                    </span>
                  );
                }
                return (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: "#fff1f0",
                      color: "#cf1322",
                      textDecoration: "line-through",
                    }}>
                    {op.text}
                  </span>
                );
              })}
            </Paragraph>
          </Card>
        </Card>
      ) : null}
    </div>
  );
};

export default UploadComponent;
